import React, { useState, useMemo, useRef, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import DateRangeSelector from "../../../daterangepicker/DateRangeSelector";

// ✅ Status pill styling
const getStatusClasses = (status) => {
  if (!status) return "bg-gray-50 text-gray-600 border-[1px] border-gray-200";

  switch (status.toLowerCase()) {
    case "success":
      return "bg-[#E8F4F2] text-[#1F6F63] border-[1px] border-[#CDE7E1]";
    case "failed":
      return "bg-[#FEF2F2] text-[#C10007] border-[1px] border-[#FFC9C9]";
    case "pending":
      return "bg-[#EFF6FF] text-[#1447E6] border-[1px] border-[#BEDBFF]";
    default:
      return "bg-gray-50 text-gray-600 border-[1px] border-gray-200";
  }
};

// Matches the appointments tab: "SUCCESS" and "Success" both read as sentence case.
const sentenceCase = (v) =>
  typeof v === "string" && v.length
    ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase()
    : v;

const PaymentHistory = ({ paymentshistory = [], isLoading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  // ✅ Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: null,
    endDate: null,
    key: "selection",
  });
  const [showCalendar, setShowCalendar] = useState(false);
 const calendarRef = useRef(null);
 useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        !event.target.closest("#calendar-toggle-btn") // allow toggle button
      ) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);
  // ✅ Auto-scroll when calendar opens
  useEffect(() => {
    if (showCalendar && calendarRef.current) {
      calendarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showCalendar]);
  // ✅ Handle Apply
  const handleDateApply = () => {
    setShowCalendar(false);
    setCurrentPage(1);
  };

  // ✅ Handle Reset
  const handleDateReset = () => {
    setSelectedDateRange({
      startDate: null,
      endDate: null,
      key: "selection",
    });
    setShowCalendar(false);
    setCurrentPage(1);
  };

  // ✅ Filtered data
const filteredData = useMemo(() => {
  return paymentshistory.filter((p) => {
    // ✅ Date filter
    let passDate = true;

    if (selectedDateRange.startDate && selectedDateRange.endDate) {
      const paymentDate = dayjs(p.created_on);

      passDate = paymentDate.isBetween(
        dayjs(selectedDateRange.startDate).startOf("day"),
        dayjs(selectedDateRange.endDate).endOf("day"),
        null,
        "[]"
      );
    }

    // ✅ Search filter
    let passSearch = true;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();

      const matchesText =
        p.payment_location?.toLowerCase().includes(term) ||
        p.payment_status?.toLowerCase().includes(term) ||
        p.method?.toLowerCase().includes(term);

      const matchesAmount = !isNaN(term)
        ? Number(p.amount) === Number(term)
        : false;

      passSearch = matchesText || matchesAmount;
    }

    return passDate && passSearch;
  });
}, [paymentshistory, searchTerm, selectedDateRange]);


  // ✅ Pagination
  const totalPages = Math.ceil(filteredData.length / perPage);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [currentPage, filteredData]);

  return (
    <div className="rounded-[16px] border border-[#EDF1F7] bg-white p-[16px] shadow-sm sm:p-6">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-[20px] font-inter font-semibold text-[#14213D]">
            Payment history
          </h2>
          <span className="text-[15px] font-inter font-medium text-[#8A94A4]">
            {filteredData.length} total
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-[10px] border border-[#E3E8EF] bg-white px-3 py-2 shadow-sm">
            <SearchIcon style={{ fontSize: 18, color: "#9AA4B5" }} />
            <input
              type="text"
              placeholder="Search payments"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-[150px] bg-transparent sm:w-[190px] font-inter text-[14px] text-[#2c3357] focus:outline-none"
            />
          </div>

          <div className="relative" ref={calendarRef}>
            <button
              id="calendar-toggle-btn"
              onClick={() => setShowCalendar((prev) => !prev)}
              className="flex items-center gap-2 rounded-[10px] border border-[#E3E8EF] bg-white px-4 py-2.5 text-[14px] font-inter font-semibold text-[#2c3357] shadow-sm hover:bg-[#F7F9FC]"
            >
              {selectedDateRange.startDate && selectedDateRange.endDate
                ? `${dayjs(selectedDateRange.startDate).format("MM/DD/YYYY")} - ${dayjs(
                    selectedDateRange.endDate
                  ).format("MM/DD/YYYY")}`
                : "Date range"}
            </button>

            {showCalendar && (
              <div className="absolute right-0 z-50 mt-2 rounded bg-white shadow-lg">
                <DateRangeSelector
                  onSubmit={handleDateApply}
                  handlereset={handleDateReset}
                  selectedDateRange={selectedDateRange}
                  setSelectedDateRange={setSelectedDateRange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-[14px] border border-[#EDF1F7]">
        <table className="w-full min-w-[640px] text-left text-sm [&_td:first-child]:whitespace-nowrap">
          <thead className="bg-[#FAFBFD] font-inter text-[11px] uppercase tracking-[0.8px] text-[#9AA4B5]">
            <tr className="border-b border-[#EDF1F7]">
              <th className="px-[20px] py-3.5 text-left font-bold">Date &amp; time</th>
              <th className="px-[20px] py-3.5 text-left font-bold">Location</th>
              <th className="px-[20px] py-3.5 text-left font-bold">Status</th>
              <th className="px-[20px] py-3.5 text-left font-bold">Amount</th>
              <th className="px-[20px] py-3.5 text-left font-bold">Method</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-[#EDF1F7]">
            {isLoading ? (
              <tr>
                <td colSpan="5" className="px-[20px] py-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : currentData.length > 0 ? (
              currentData.map((p, idx) => (
                <tr
                  key={p.id ?? `${p.created_on}-${p.amount}-${idx}`}
                  className="align-middle hover:bg-[#FAFBFD]"
                >
                  {/* Date over time, the same two-line cell the appointments tab uses */}
                  <td className="px-[20px] py-4 align-middle">
                    <div className="flex flex-col justify-start">
                      <div className="font-inter text-[14px] font-bold text-[#14213D]">
                        {p.created_on
                          ? dayjs(p.created_on).format("MMM D, YYYY")
                          : "\u2014"}
                      </div>
                      {p.created_at && (
                        <div className="mt-0.5 font-inter text-[13px] font-medium text-[#64748B]">
                          {p.created_at}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-[20px] py-4 align-middle font-inter text-[14px] text-[#48526A]">
                    {p.payment_location || "\u2014"}
                  </td>
                  <td className="px-[20px] py-4 align-middle">
                    {p.payment_status ? (
                      <span
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-inter text-[13px] font-semibold ${getStatusClasses(
                          p.payment_status
                        )}`}
                      >
                        <span
                          aria-hidden="true"
                          className="h-[7px] w-[7px] rounded-full bg-current"
                        />
                        {sentenceCase(p.payment_status)}
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className="px-[20px] py-4 align-middle font-inter text-[14px] font-bold text-[#14213D]">
                    ${parseFloat(p.amount || 0).toFixed(2)}
                  </td>
                  <td className="px-[20px] py-4 align-middle font-inter text-[14px] text-[#48526A]">
                    {p.method || "\u2014"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-[20px] py-6 text-center text-gray-500">
                  No payment history found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination, inside the table card as on the appointments tab */}
        {!isLoading && filteredData.length > 0 && (
          <div className="flex items-center justify-between border-t border-[#EDF1F7] px-[20px] py-4 text-sm">
            <span className="font-inter text-[14px] text-[#64748B]">
              Showing {(currentPage - 1) * perPage + 1}
              &ndash;
              {Math.min(currentPage * perPage, filteredData.length)} of{" "}
              {filteredData.length}
            </span>
            <div className="flex gap-2">
              <button
                className="rounded-[10px] border border-[#E3E8EF] px-4 py-2 font-inter text-[14px] font-semibold text-[#2c3357] hover:bg-[#F7F9FC] disabled:opacity-40"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </button>
              <button
                className="rounded-[10px] border border-[#E3E8EF] px-4 py-2 font-inter text-[14px] font-semibold text-[#2c3357] hover:bg-[#F7F9FC] disabled:opacity-40"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
